/**
 * ConopsMapEditor — Visual CONOPS editor for DACAMS FTS module.
 *
 * Renders UnifiedAirportMap (fts-conops mode) with a ConopsPlugin child that
 * owns the conops-features GeoJSON source and MapboxDraw instance. All tool
 * activation, panel state and feature management remain here.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import type MapboxDraw from '@mapbox/mapbox-gl-draw';

import {
  MapTrifold,
  CaretDown,
  CaretRight,
  PencilSimple,
  Trash,
  ArrowFatDown,
  ArrowFatUp,
  ArrowCounterClockwise,
  Warning,
} from '@phosphor-icons/react';

import TopologyImportModal from './TopologyImportModal';
import ConopsPlugin from './ConopsPlugin';
import UnifiedAirportMap from '../map-engine/UnifiedAirportMap';

import { useTheme } from '../../context/ThemeContext';
import { conopsService } from '../../services/conops';
import { useMapStore } from '../../store/mapStore';

import ConopsMapToolbar from './ConopsMapToolbar';
import ConopsFeaturePanel from './ConopsFeaturePanel';
import TaxiRouteTool from './TaxiRouteTool';

import type {
  ActiveTool,
  AirportTopology,
  ConopsFeature,
  ConopsGeoJSON,
  ConopsProperties,
  ConopsFeatureType,
  AmsZoneProperties,
  HandoverPointProperties,
  PushbackProfileProperties,
  TaxiRouteProperties,
  Movement,
} from '../../types/conops';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function generateId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}`;
}

function extractTaxiwayIds(topology: AirportTopology): string[] {
  const source = topology.imported_topology ?? topology.taxiways;
  return source.features
    .filter((f) => f.properties?.aeroway === 'taxiway')
    .map((f) => f.properties?.ref || f.properties?.name || '')
    .filter(Boolean) as string[];
}

function createHandoverMarkerEl(movement: Movement): HTMLDivElement {
  const el = document.createElement('div');
  const isArr = movement === 'ARR';
  el.style.cssText = `
    width: 28px; height: 28px; border-radius: 50%;
    background-color: ${isArr ? '#22c55e' : '#F26430'};
    border: 3px solid ${isArr ? '#16a34a' : '#c2410c'};
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,0.5);
  `;
  const arrow = document.createElement('span');
  arrow.style.cssText = 'color: white; font-size: 14px; line-height: 1; font-weight: bold;';
  arrow.textContent = isArr ? '↓' : '↑';
  el.appendChild(arrow);
  return el;
}

// ─── Feature list section ─────────────────────────────────────────────────────

interface FeatureListSectionProps {
  title: string;
  features: ConopsFeature[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  isDark: boolean;
  renderBadge?: (f: ConopsFeature) => React.ReactNode;
}

const FeatureListSection: React.FC<FeatureListSectionProps> = ({
  title, features, onEdit, onDelete, isDark, renderBadge,
}) => {
  const [open, setOpen] = useState(true);
  if (features.length === 0) return null;

  return (
    <div className="mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide hover:bg-[var(--surface-raised)] rounded transition-colors"
      >
        {open ? <CaretDown size={10} weight="bold" /> : <CaretRight size={10} weight="bold" />}
        {title} ({features.length})
      </button>
      {open && (
        <div className="space-y-0.5 pl-4">
          {features.map((f) => {
            const id = f.properties.id;
            const name = (f.properties as any).name || id;
            return (
              <div
                key={id}
                className={`flex items-center justify-between px-2 py-1.5 rounded text-xs group ${isDark ? 'hover:bg-[#2a2a2a]' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {renderBadge && renderBadge(f)}
                  <span className="text-[var(--text-primary)] truncate font-mono">{name}</span>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => onEdit(id)} className="p-1 rounded hover:bg-[var(--surface)] text-[var(--text-secondary)]">
                    <PencilSimple size={12} weight="bold" />
                  </button>
                  <button onClick={() => onDelete(id)} className="p-1 rounded hover:bg-red-500/10 text-red-400">
                    <Trash size={12} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────

interface ConopsMapEditorProps {
  icaoCode: string;
  scenarioId?: string;
  value: ConopsGeoJSON | null;
  onChange: (geojson: ConopsGeoJSON) => void;
}

const ConopsMapEditor: React.FC<ConopsMapEditorProps> = ({
  icaoCode,
  scenarioId,
  value,
  onChange,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Map + draw refs (populated by callbacks from UnifiedAirportMap / ConopsPlugin)
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const drawRef = useRef<InstanceType<typeof MapboxDraw> | null>(null);
  const markersRef = useRef<Map<string, mapboxgl.Marker>>(new Map());
  const clickListenerRef = useRef<((e: mapboxgl.MapMouseEvent) => void) | null>(null);

  // Local topology — only for taxiwayIds + findNearestStand; rendering is handled by BaseAirportLayer
  const [topology, setTopology] = useState<AirportTopology | null>(null);
  const [topoKey, setTopoKey] = useState(0);

  // Feature state
  const [features, setFeatures] = useState<ConopsFeature[]>(value?.features || []);
  const [customTaxiways, setCustomTaxiways] = useState<GeoJSON.Feature[]>([]);
  const [activeTool, setActiveTool] = useState<ActiveTool>('none');
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<'create' | 'edit' | null>(null);
  const [panelFeatureType, setPanelFeatureType] = useState<ConopsFeatureType | null>(null);
  const [panelInitialData, setPanelInitialData] = useState<Partial<ConopsProperties> | undefined>();
  const [pendingGeometry, setPendingGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [pendingDrawId, setPendingDrawId] = useState<string | null>(null);
  const [taxiRouteMeta, setTaxiRouteMeta] = useState<Partial<TaxiRouteProperties>>({});
  const [taxiRouteSequence, setTaxiRouteSequence] = useState<string[]>([]);
  const [customTwyPanelOpen, setCustomTwyPanelOpen] = useState(false);
  const [customTwyRef, setCustomTwyRef] = useState('');
  const [customTwyName, setCustomTwyName] = useState('');
  const [pendingCustomTwyGeom, setPendingCustomTwyGeom] = useState<GeoJSON.Geometry | null>(null);
  const [pendingCustomTwyDrawId, setPendingCustomTwyDrawId] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Subscribe to layer visibility to show/hide handover markers
  const layerVisibility = useMapStore((s) => s.layerVisibility);

  const taxiwayIds = topology ? extractTaxiwayIds(topology) : [];
  const aorZones = features
    .filter((f) => f.properties.feature_type === 'ams_zone')
    .map((f) => f.properties as AmsZoneProperties);

  // ─── Topology (local copy for UI helpers) ────────────────────────────────────

  useEffect(() => {
    if (!icaoCode) return;
    conopsService.getTopology(icaoCode).then((topo) => {
      setTopology(topo);
      setCustomTaxiways(
        topo.custom_taxiways?.features?.length
          ? (topo.custom_taxiways.features as GeoJSON.Feature[])
          : [],
      );
    }).catch(() => {});
  }, [icaoCode, topoKey]);

  // ─── Handover marker visibility ───────────────────────────────────────────────

  useEffect(() => {
    const vis = layerVisibility['conops-handover-points'] ?? true;
    markersRef.current.forEach((m) => {
      m.getElement().style.display = vis ? '' : 'none';
    });
  }, [layerVisibility]);

  // ─── Cleanup markers on unmount ───────────────────────────────────────────────

  useEffect(() => {
    return () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current.clear();
    };
  }, []);

  // ─── Emit upward ────────────────────────────────────────────────────────────

  const emitChange = useCallback(
    (updatedFeatures: ConopsFeature[]) => {
      onChange({
        type: 'FeatureCollection',
        airport_icao: icaoCode,
        ci_node_safety_threshold: value?.ci_node_safety_threshold ?? 8.5,
        sequencing_priority: value?.sequencing_priority ?? 'AUTO',
        features: updatedFeatures,
      });
    },
    [icaoCode, onChange, value?.ci_node_safety_threshold, value?.sequencing_priority],
  );

  // ─── draw.create handler (called by ConopsPlugin via onDrawCreate prop) ───────

  const handleDrawCreate = useCallback(
    (geom: GeoJSON.Geometry, drawnId: string) => {
      if (activeTool === 'aor') {
        setPendingGeometry(geom);
        setPendingDrawId(drawnId);
        setPanelFeatureType('ams_zone');
        setPanelInitialData({ feature_type: 'ams_zone', id: generateId('APN'), name: '', color: '#4A90D9', stands: [], taxiways: [] });
        setPanelMode('create');
      } else if (activeTool === 'taxi_route') {
        setPendingGeometry(geom);
        setPendingDrawId(drawnId);
      } else if (activeTool === 'aptr_blue' || activeTool === 'aptr_orange') {
        setPendingGeometry(geom);
        setPendingDrawId(drawnId);
        setPanelFeatureType(activeTool);
        setPanelInitialData({
          feature_type: activeTool,
          id: generateId(activeTool === 'aptr_blue' ? 'APTB' : 'APTO'),
          name: activeTool === 'aptr_blue' ? 'APTR Azul' : 'APTR Naranja',
          icao_cat_max: 'C',
        });
        setPanelMode('create');
      } else if (activeTool === 'roundabout') {
        setPendingGeometry(geom);
        setPendingDrawId(drawnId);
        setPanelFeatureType('roundabout');
        setPanelInitialData({ feature_type: 'roundabout', id: generateId('RAB'), name: 'Roundabout', icao_cat_max: 'C' });
        setPanelMode('create');
      } else if (activeTool === 'custom_taxiway') {
        setPendingCustomTwyGeom(geom);
        setPendingCustomTwyDrawId(drawnId);
        setCustomTwyRef('');
        setCustomTwyName('');
        setCustomTwyPanelOpen(true);
      }
    },
    [activeTool],
  );

  // ─── Tool activation ──────────────────────────────────────────────────────────

  const removePendingClickListener = () => {
    if (clickListenerRef.current && mapRef.current) {
      mapRef.current.off('click', clickListenerRef.current);
      clickListenerRef.current = null;
    }
  };

  const resetCursor = () => {
    if (mapRef.current) mapRef.current.getCanvas().style.cursor = '';
  };

  const handleToolChange = useCallback(
    (tool: ActiveTool) => {
      const map = mapRef.current;
      const draw = drawRef.current;

      removePendingClickListener();
      resetCursor();
      if (draw) { try { draw.changeMode('simple_select'); } catch (_) {} }

      setActiveTool(tool);
      if (tool !== 'taxi_route') {
        setPanelMode(null);
        setPanelFeatureType(null);
      }

      if (!map) return;

      switch (tool) {
        case 'aor':
          if (draw) { try { draw.changeMode('draw_polygon'); } catch (_) {} }
          break;

        case 'handover_arr':
        case 'handover_dep': {
          map.getCanvas().style.cursor = 'crosshair';
          const movement: Movement = tool === 'handover_arr' ? 'ARR' : 'DEP';
          const listener = (e: mapboxgl.MapMouseEvent) => {
            const { lng, lat } = e.lngLat;
            removePendingClickListener();
            resetCursor();
            setPendingGeometry({ type: 'Point', coordinates: [lng, lat] });
            setPanelFeatureType('handover_point');
            setPanelInitialData({
              feature_type: 'handover_point', id: generateId('HP'), name: '', movement,
              rwy_config: 'SOUTH', runway: '', zone_id: '', contact_probability: 0.8, contact_delay_s: 30,
            });
            setPanelMode('create');
          };
          clickListenerRef.current = listener;
          map.once('click', listener);
          break;
        }

        case 'pushback': {
          map.getCanvas().style.cursor = 'crosshair';
          let firstClick: mapboxgl.LngLat | null = null;
          const twoClickListener = (e: mapboxgl.MapMouseEvent) => {
            if (!firstClick) {
              firstClick = e.lngLat;
            } else {
              const start: GeoJSON.Position = [firstClick.lng, firstClick.lat];
              const end: GeoJSON.Position = [e.lngLat.lng, e.lngLat.lat];
              map.off('click', twoClickListener);
              clickListenerRef.current = null;
              resetCursor();
              setPendingGeometry({ type: 'LineString', coordinates: [start, end] });
              const nearestStand = findNearestStand(topology, firstClick);
              setPanelFeatureType('pushback_profile');
              setPanelInitialData({
                feature_type: 'pushback_profile', id: generateId('PB'),
                stand: nearestStand, direction: '', entry_taxiway: '', standard: true,
              });
              setPanelMode('create');
            }
          };
          clickListenerRef.current = twoClickListener;
          map.on('click', twoClickListener);
          break;
        }

        case 'taxi_route':
          setPanelFeatureType('taxi_route');
          setPanelMode('create');
          break;

        case 'aptr_blue':
        case 'aptr_orange':
        case 'custom_taxiway':
          if (draw) { try { draw.changeMode('draw_line_string'); } catch (_) {} }
          break;

        case 'roundabout':
          if (draw) { try { draw.changeMode('draw_polygon'); } catch (_) {} }
          break;

        default: break;
      }
    },
    [topology],
  );

  function findNearestStand(topo: AirportTopology | null, lngLat: mapboxgl.LngLat): string {
    if (!topo || !topo.stands.features.length) return '';
    let minDist = Infinity;
    let nearest = '';
    for (const f of topo.stands.features) {
      if (f.geometry.type !== 'Point') continue;
      const [sLng, sLat] = (f.geometry as GeoJSON.Point).coordinates;
      const d = Math.hypot(sLng - lngLat.lng, sLat - lngLat.lat);
      if (d < minDist) { minDist = d; nearest = f.properties?.ref || f.properties?.name || ''; }
    }
    return nearest;
  }

  function addHandoverMarker(map: mapboxgl.Map, featureId: string, movement: Movement, lng: number, lat: number) {
    const el = createHandoverMarkerEl(movement);
    const marker = new mapboxgl.Marker({ element: el }).setLngLat([lng, lat]).addTo(map);
    markersRef.current.set(featureId, marker);
  }

  // ─── Panel save ───────────────────────────────────────────────────────────────

  const handlePanelSave = useCallback(
    (props: ConopsProperties) => {
      if (!pendingGeometry) return;

      const newFeature: ConopsFeature = { type: 'Feature', id: props.id, geometry: pendingGeometry, properties: props };
      const updated = [...features, newFeature];
      setFeatures(updated);
      emitChange(updated);

      if (props.feature_type === 'handover_point' && pendingGeometry.type === 'Point') {
        const [lng, lat] = (pendingGeometry as GeoJSON.Point).coordinates;
        if (mapRef.current) addHandoverMarker(mapRef.current, props.id, (props as HandoverPointProperties).movement, lng, lat);
      }

      if (pendingDrawId && drawRef.current) { try { drawRef.current.delete(pendingDrawId); } catch (_) {} }

      if (props.feature_type === 'taxi_route') {
        const merged: ConopsFeature = { ...newFeature, properties: { ...props, sequence: taxiRouteSequence } as TaxiRouteProperties };
        const withRoute = updated.map((f) => (f.id === merged.id ? merged : f));
        setFeatures(withRoute);
        emitChange(withRoute);
      }

      setPendingGeometry(null);
      setPendingDrawId(null);
      setPanelMode(null);
      setPanelFeatureType(null);
      setActiveTool('none');
    },
    [pendingGeometry, pendingDrawId, features, taxiRouteSequence, emitChange],
  );

  const handleTaxiRouteSave = () => {
    if (!pendingGeometry) return;
    const props: TaxiRouteProperties = {
      feature_type: 'taxi_route',
      id: taxiRouteMeta.id || generateId('TR'),
      name: taxiRouteMeta.name || '',
      rwy_config: taxiRouteMeta.rwy_config || 'SOUTH',
      runway: taxiRouteMeta.runway || '',
      movement: taxiRouteMeta.movement || 'ARR',
      zone_id: taxiRouteMeta.zone_id || '',
      lvp_mandatory: taxiRouteMeta.lvp_mandatory || false,
      cat_max: taxiRouteMeta.cat_max || 'C',
      sequence: taxiRouteSequence,
    };
    handlePanelSave(props);
  };

  // ─── Edit / Delete ────────────────────────────────────────────────────────────

  const handleFeatureEdit = (featureId: string) => {
    const f = features.find((feat) => feat.properties.id === featureId);
    if (!f) return;
    setSelectedFeatureId(featureId);
    setPendingGeometry(f.geometry);
    setPanelFeatureType(f.properties.feature_type);
    setPanelInitialData(f.properties);
    setPanelMode('edit');
    if (mapRef.current && f.geometry.type === 'Point') {
      const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates;
      mapRef.current.flyTo({ center: [lng, lat], zoom: 15 });
    }
  };

  const handleFeatureDelete = (featureId: string) => {
    const updated = features.filter((f) => f.properties.id !== featureId);
    setFeatures(updated);
    emitChange(updated);
    const marker = markersRef.current.get(featureId);
    if (marker) { marker.remove(); markersRef.current.delete(featureId); }
    if (selectedFeatureId === featureId) { setPanelMode(null); setSelectedFeatureId(null); }
  };

  const handleEditSave = (props: ConopsProperties) => {
    if (!selectedFeatureId) return;
    const updated = features.map((f) => f.properties.id === selectedFeatureId ? { ...f, properties: props } : f);
    setFeatures(updated);
    emitChange(updated);
    setPanelMode(null);
    setSelectedFeatureId(null);
  };

  const handlePanelClose = () => {
    setPanelMode(null);
    setPanelFeatureType(null);
    setPendingGeometry(null);
    setPendingDrawId(null);
    setActiveTool('none');
    resetCursor();
    if (drawRef.current) { try { drawRef.current.changeMode('simple_select'); } catch (_) {} }
  };

  const handleClearAll = () => {
    setFeatures([]);
    emitChange([]);
    markersRef.current.forEach((m) => m.remove());
    markersRef.current.clear();
    if (drawRef.current) { try { drawRef.current.deleteAll(); } catch (_) {} }
    setPanelMode(null);
  };

  const handleCustomTwySave = useCallback(async () => {
    if (!pendingCustomTwyGeom) return;
    const id = generateId('CTW');
    const feature: GeoJSON.Feature = {
      type: 'Feature',
      id,
      geometry: pendingCustomTwyGeom,
      properties: { id, ref: customTwyRef || id, name: customTwyName || customTwyRef || id, aeroway: 'taxiway' },
    };
    try {
      const updated = await conopsService.saveCustomTaxiway(icaoCode, feature);
      setCustomTaxiways(updated.features as GeoJSON.Feature[]);
    } catch (err) {
      console.error('[ConopsMapEditor] saveCustomTaxiway failed:', err);
    }
    if (pendingCustomTwyDrawId && drawRef.current) {
      try { drawRef.current.delete(pendingCustomTwyDrawId); } catch (_) {}
    }
    setPendingCustomTwyGeom(null);
    setPendingCustomTwyDrawId(null);
    setCustomTwyPanelOpen(false);
    setActiveTool('none');
    if (drawRef.current) { try { drawRef.current.changeMode('simple_select'); } catch (_) {} }
  }, [pendingCustomTwyGeom, pendingCustomTwyDrawId, customTwyRef, customTwyName, icaoCode]);

  const handleCustomTwyDelete = useCallback(async (featureId: string) => {
    try {
      const updated = await conopsService.deleteCustomTaxiway(icaoCode, featureId);
      setCustomTaxiways(updated.features as GeoJSON.Feature[]);
    } catch (err) {
      console.error('[ConopsMapEditor] deleteCustomTaxiway failed:', err);
    }
  }, [icaoCode]);

  const handleSaveToBackend = useCallback(async () => {
    if (!scenarioId || !features.length) return;
    try {
      await conopsService.saveConops(scenarioId, { type: 'FeatureCollection', airport_icao: icaoCode, features });
    } catch (err) {
      console.error('[ConopsMapEditor] saveConops failed:', err);
    }
  }, [scenarioId, features, icaoCode]);

  const handleTopologyImported = useCallback(() => {
    setTopoKey((k) => k + 1);
  }, []);

  const handleResetTopology = useCallback(async () => {
    setResetError(null);
    setResetLoading(true);
    try {
      await conopsService.resetImportedTopology(icaoCode);
      setTopoKey((k) => k + 1);
    } catch (err: any) {
      const msg = err?.response?.data?.detail ?? err?.message ?? 'Error al conectar con el servidor.';
      setResetError(msg);
      console.error('[ConopsMapEditor] resetImportedTopology failed:', err);
    } finally {
      setResetLoading(false);
    }
  }, [icaoCode]);

  // ─── Derived ──────────────────────────────────────────────────────────────────

  const aorFeatures      = features.filter((f) => f.properties.feature_type === 'ams_zone');
  const handoverFeatures = features.filter((f) => f.properties.feature_type === 'handover_point');
  const pushbackFeatures = features.filter((f) => f.properties.feature_type === 'pushback_profile');
  const taxiFeatures     = features.filter((f) => f.properties.feature_type === 'taxi_route');
  const hasAnyFeatures   = features.length > 0 || customTaxiways.length > 0;

  const customTaxiwaysFC: GeoJSON.FeatureCollection | null =
    customTaxiways.length > 0
      ? { type: 'FeatureCollection', features: customTaxiways }
      : null;

  // ─── Empty state ──────────────────────────────────────────────────────────────

  if (!icaoCode) {
    return (
      <div className={`flex flex-col items-center justify-center h-64 rounded-xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface)]' : 'border-gray-200 bg-gray-50'}`}>
        <MapTrifold size={40} className="text-[var(--text-tertiary)] mb-3" />
        <p className="text-sm text-[var(--text-secondary)] text-center px-4">
          Selecciona un proyecto para activar el editor de mapa
        </p>
      </div>
    );
  }

  const panelOpen = panelMode !== null;
  const isTaxiRoutePanel = panelFeatureType === 'taxi_route';

  return (
    <>
      <div className={`flex flex-col rounded-xl border overflow-hidden ${isDark ? 'border-[var(--border)] bg-[#0f0f0f]' : 'border-gray-200 bg-white'}`}>

        {/* Imported topology warning — full-width row ABOVE the map, no z-index fights */}
        {topology?.imported_topology && (
          <div
            className="flex items-center gap-2 px-3 py-2 text-xs font-semibold border-b"
            style={{
              background: resetError ? 'rgba(239,68,68,0.12)' : 'rgba(251,191,36,0.08)',
              borderColor: resetError ? 'rgba(239,68,68,0.35)' : 'rgba(251,191,36,0.3)',
              color: resetError ? '#f87171' : '#fbbf24',
            }}
          >
            <Warning size={14} weight="fill" className="flex-shrink-0" />
            {resetError ? (
              <span className="flex-1">
                Error al resetear: {resetError} — Reinicia el servidor backend e inténtalo de nuevo
              </span>
            ) : (
              <span className="flex-1">
                Topología importada activa
                ({topology.imported_topology_source === 'aixm' ? 'AIXM' : topology.imported_topology_source === 'geojson' ? 'GeoJSON' : 'AIP PDF'})
                {' — '}Esta topología se comparte con todos los escenarios de {icaoCode}
              </span>
            )}
            <button
              onClick={handleResetTopology}
              disabled={resetLoading}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50 flex-shrink-0 font-bold text-xs"
              style={{
                background: resetError ? 'rgba(239,68,68,0.15)' : 'rgba(251,191,36,0.15)',
                borderColor: resetError ? 'rgba(239,68,68,0.4)' : 'rgba(251,191,36,0.4)',
                color: resetError ? '#f87171' : '#fbbf24',
              }}
            >
              <ArrowCounterClockwise size={12} weight="bold" className={resetLoading ? 'animate-spin' : ''} />
              {resetLoading ? 'Reseteando…' : 'Resetear mapa'}
            </button>
          </div>
        )}

        {/* Map area */}
        <div className="relative" style={{ height: '520px' }}>
          <UnifiedAirportMap
            mode="fts-conops"
            icaoCode={icaoCode}
            onMapReady={(m) => { mapRef.current = m; }}
            topologyRefreshKey={topoKey}
            className="absolute inset-0"
            style={{ height: '100%' }}
          >
            {/* CONOPS drawing layer + MapboxDraw */}
            <ConopsPlugin
              features={features}
              customTaxiwaysFC={customTaxiwaysFC}
              onDrawReady={(draw) => { drawRef.current = draw; }}
              onDrawCreate={handleDrawCreate}
            />

            {/* Left toolbar */}
            <ConopsMapToolbar
              activeTool={activeTool}
              onToolChange={handleToolChange}
              onClearAll={handleClearAll}
              onImport={() => setImportModalOpen(true)}
              onResetTopology={handleResetTopology}
              topologyImported={!!topology?.imported_topology}
              topologySource={topology?.imported_topology_source}
              resetLoading={resetLoading}
              isDark={isDark}
            />

            {/* Feature metadata panel */}
            {panelOpen && !isTaxiRoutePanel && (
              <ConopsFeaturePanel
                mode={panelMode}
                featureType={panelFeatureType}
                initialData={panelInitialData}
                existingZones={aorZones}
                topologyTaxiways={taxiwayIds}
                onSave={panelMode === 'edit' ? handleEditSave : handlePanelSave}
                onDelete={panelMode === 'edit' && selectedFeatureId ? () => handleFeatureDelete(selectedFeatureId) : undefined}
                onClose={handlePanelClose}
                isDark={isDark}
              />
            )}

            {/* Custom taxiway mini-panel */}
            {customTwyPanelOpen && (
              <div
                className={`absolute right-0 top-0 z-10 flex flex-col border-l shadow-xl ${isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'}`}
                style={{ width: '280px' }}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
                  <span className="text-sm font-bold text-[#FFD700] uppercase tracking-wide">TWY Personalizada</span>
                  <button
                    onClick={() => {
                      setCustomTwyPanelOpen(false);
                      setPendingCustomTwyGeom(null);
                      if (pendingCustomTwyDrawId && drawRef.current) { try { drawRef.current.delete(pendingCustomTwyDrawId); } catch (_) {} }
                      setPendingCustomTwyDrawId(null);
                      setActiveTool('none');
                      if (drawRef.current) { try { drawRef.current.changeMode('simple_select'); } catch (_) {} }
                    }}
                    className="p-1 rounded hover:bg-[var(--surface-raised)] text-[var(--text-secondary)] text-xs font-bold"
                  >
                    X
                  </button>
                </div>
                <div className="px-4 py-3 space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
                      Designador (ref)
                    </label>
                    <input
                      className="w-full px-2.5 py-1.5 rounded-md border border-[var(--border)] bg-[var(--surface-inset)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD700]"
                      value={customTwyRef}
                      placeholder="S2"
                      autoFocus
                      onChange={(e) => setCustomTwyRef(e.target.value.toUpperCase())}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1 uppercase tracking-wide">
                      Nombre (opcional)
                    </label>
                    <input
                      className="w-full px-2.5 py-1.5 rounded-md border border-[var(--border)] bg-[var(--surface-inset)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[#FFD700]"
                      value={customTwyName}
                      placeholder="Calle de rodaje S2"
                      onChange={(e) => setCustomTwyName(e.target.value)}
                    />
                  </div>
                </div>
                <div className="px-4 pb-4">
                  <button
                    onClick={handleCustomTwySave}
                    disabled={!customTwyRef}
                    className="w-full py-2 rounded-lg text-sm font-bold transition-colors disabled:opacity-40"
                    style={{ backgroundColor: '#FFD700', color: '#0d0d14' }}
                  >
                    Guardar TWY
                  </button>
                </div>
              </div>
            )}

            {/* Taxi route panel */}
            {panelOpen && isTaxiRoutePanel && (
              <div
                className={`absolute right-0 top-0 bottom-0 z-10 flex flex-col border-l shadow-xl ${isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-200'}`}
                style={{ width: '320px' }}
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
                  <span className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wide">Ruta de Rodaje</span>
                  <button onClick={handlePanelClose} className="p-1 rounded hover:bg-[var(--surface-raised)] text-[var(--text-secondary)]">
                    <span className="text-xs font-bold">X</span>
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <TaxiRouteTool
                    icaoCode={icaoCode}
                    topologyTaxiways={taxiwayIds}
                    existingZones={aorZones}
                    map={mapRef.current}
                    drawRef={drawRef}
                    onRouteReady={(seq) => setTaxiRouteSequence(seq)}
                    onMetadataChange={(meta) => setTaxiRouteMeta(meta)}
                    isDark={isDark}
                  />
                </div>
                {pendingGeometry && (
                  <div className="px-4 py-3 border-t border-[var(--border)]">
                    <button onClick={handleTaxiRouteSave} className="w-full py-2 rounded-lg bg-[#F26430] hover:bg-[#d9542a] text-white text-sm font-bold transition-colors">
                      Guardar Ruta
                    </button>
                  </div>
                )}
              </div>
            )}
          </UnifiedAirportMap>
        </div>

        {/* Feature list */}
        {hasAnyFeatures && (
          <div className={`border-t px-4 py-3 max-h-48 overflow-y-auto ${isDark ? 'border-[var(--border)] bg-[#0d0d0d]' : 'border-gray-100 bg-gray-50'}`}>
            <div className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide mb-2">Elementos definidos</div>

            <FeatureListSection
              title="Zonas AoR" features={aorFeatures} onEdit={handleFeatureEdit} onDelete={handleFeatureDelete} isDark={isDark}
              renderBadge={(f) => {
                const color = (f.properties as AmsZoneProperties).color;
                return <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />;
              }}
            />
            <FeatureListSection
              title="Puntos Transferencia" features={handoverFeatures} onEdit={handleFeatureEdit} onDelete={handleFeatureDelete} isDark={isDark}
              renderBadge={(f) => {
                const p = f.properties as HandoverPointProperties;
                return (
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${p.movement === 'ARR' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>
                    {p.movement === 'ARR' ? <ArrowFatDown size={10} weight="fill" /> : <ArrowFatUp size={10} weight="fill" />}
                  </span>
                );
              }}
            />
            <FeatureListSection title="Retrocesos" features={pushbackFeatures} onEdit={handleFeatureEdit} onDelete={handleFeatureDelete} isDark={isDark} />
            <FeatureListSection
              title="Rutas de Rodaje" features={taxiFeatures} onEdit={handleFeatureEdit} onDelete={handleFeatureDelete} isDark={isDark}
              renderBadge={(f) => {
                const p = f.properties as TaxiRouteProperties;
                return (
                  <>
                    <span className={`text-xs font-bold px-1 rounded ${p.movement === 'ARR' ? 'bg-green-500/20 text-green-400' : 'bg-orange-500/20 text-orange-400'}`}>{p.movement}</span>
                    {p.lvp_mandatory && <span className="text-xs font-bold px-1 rounded bg-amber-500/20 text-amber-400">LVP</span>}
                  </>
                );
              }}
            />

            {/* Custom topology taxiways */}
            {customTaxiways.length > 0 && (
              <div className="mb-2">
                <div className="px-3 py-1.5 text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wide flex items-center gap-1.5">
                  <span className="w-4 h-0.5 rounded inline-block flex-shrink-0" style={{ backgroundColor: '#FFD700' }} />
                  TWY Personalizadas ({customTaxiways.length})
                </div>
                <div className="space-y-0.5 pl-4">
                  {customTaxiways.map((f) => {
                    const id = (f.properties as any)?.id || '';
                    const ref = (f.properties as any)?.ref || id;
                    const name = (f.properties as any)?.name || ref;
                    return (
                      <div
                        key={id}
                        className={`flex items-center justify-between px-2 py-1.5 rounded text-xs group ${isDark ? 'hover:bg-[#2a2a2a]' : 'hover:bg-gray-50'}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded font-mono" style={{ backgroundColor: '#FFD70020', color: '#FFD700' }}>{ref}</span>
                          <span className="text-[var(--text-primary)] truncate">{name !== ref ? name : ''}</span>
                        </div>
                        <button
                          onClick={() => handleCustomTwyDelete(id)}
                          className="p-1 rounded hover:bg-red-500/10 text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Motor parameters footer */}
        <div className={`border-t px-4 py-3 ${isDark ? 'border-[var(--border)]' : 'border-gray-200'}`}>
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <label className={`text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${isDark ? 'text-[var(--text-secondary)]' : 'text-gray-500'}`}>
                CI_node threshold
              </label>
              <input
                type="number" min={0} step={0.5}
                value={value?.ci_node_safety_threshold ?? 8.5}
                onChange={(e) => onChange({ type: 'FeatureCollection', airport_icao: icaoCode, ...value, features, ci_node_safety_threshold: parseFloat(e.target.value) || 0 })}
                className={`w-20 text-sm rounded px-2 py-1 border outline-none ${isDark ? 'bg-[var(--surface-inset)] border-[var(--border)] text-[var(--text-primary)]' : 'bg-gray-50 border-gray-300 text-gray-800'} focus:border-[#F26430]`}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className={`text-xs font-semibold uppercase tracking-wide whitespace-nowrap ${isDark ? 'text-[var(--text-secondary)]' : 'text-gray-500'}`}>
                Secuenciación
              </label>
              <div className="flex gap-1">
                {(['AUTO', 'EOBT', 'CTOT'] as const).map((opt) => (
                  <button
                    key={opt} type="button"
                    onClick={() => onChange({ type: 'FeatureCollection', airport_icao: icaoCode, ...value, features, sequencing_priority: opt })}
                    className={`px-2 py-1 rounded text-xs font-bold border transition-colors ${
                      (value?.sequencing_priority ?? 'AUTO') === opt
                        ? 'bg-[#F26430] border-[#F26430] text-white'
                        : isDark ? 'border-[var(--border)] text-[var(--text-secondary)] hover:border-[#F26430]' : 'border-gray-300 text-gray-600 hover:border-[#F26430]'
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            {scenarioId && features.length > 0 && (
              <button
                onClick={handleSaveToBackend}
                className="ml-auto text-xs font-semibold px-3 py-1.5 rounded bg-[#F26430]/10 text-[#F26430] border border-[#F26430]/30 hover:bg-[#F26430]/20 transition-colors"
              >
                Guardar CONOPS en escenario
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Topology import modal */}
      {importModalOpen && (
        <TopologyImportModal
          icaoCode={icaoCode}
          onImported={handleTopologyImported}
          onClose={() => setImportModalOpen(false)}
        />
      )}
    </>
  );
};

export default ConopsMapEditor;
